/* prettier-ignore */

import { inject, Injectable, NgZone, signal } from '@angular/core';
import { AudioStorageService } from './audio-storage.service';
import { Subject } from 'rxjs';
import { DebugService } from 'src/app/services/debug.service';

type Backpressure = 'drop' | 'allow';

@Injectable({ providedIn: 'root' })

export class PlayAudioHandlerService {
  private audioStorageService = inject(AudioStorageService);
  private debugService = inject(DebugService);
  private ngZone = inject(NgZone);

  // ---- WebAudio
  private ctx!: AudioContext;
  private gain!: GainNode;

  // ---- Cola / temporización
  private lastWhen = 0;            // tiempo de fin programado del último buffer
  private queuedSec = 0;           // segundos pendientes (lastWhen - now)
  private sources: AudioBufferSourceNode[] = [];

  // ---- Señales públicas
  public readonly isPlaying = signal(false);

  // ---- Config
  private readonly inRate = 24000;
  private readonly channels = 1;
  private readonly startThresholdSec = 0.25;
  private readonly maxQueueSec = 12;
  private readonly safetyPadSec = 0.05;
  private readonly graceMs = 3000;
  private readonly backpressure: Backpressure = 'allow';

  // ---- Estado de sesión
  private epoch = 0;
  private started = false;
  private closed = false;
  private finished = false;
  private lastChunkTs = 0;

  // ---- Watchdog
  private checkTimer: any = null;
  private lastMaybeMs = 0;

  // ---- Evento de fin real
  private finishedSubj = new Subject<void>();
  public readonly finished$ = this.finishedSubj.asObservable();

  // ---- Scratch buffer reutilizable (opcional)
  private scratchF32: Float32Array | null = null;

  // ---- Tail
  private tailScheduled = false;
  private tailEverInjected = false;

  // ---- Audio externo (TTS stream)
  private externalAudio: HTMLAudioElement | null = null;

  // ---- Audio on demand
  public isPlayingOnDemand = signal(null);
  private currentAudioOnDemandElement: any;

  async startSession(): Promise<void> {
    await this.ensureContext();
    this.newEpoch();

    // limpia fuentes colgantes
    for (const s of this.sources) {
      try { s.onended = null; s.stop(); } catch { }
    }
    this.sources = [];

    // reset
    this.lastWhen = this.ctx.currentTime;
    this.queuedSec = 0;
    this.isPlaying.set(false);
    this.started = false;
    this.closed = false;
    this.finished = false;
    this.lastChunkTs = 0;
    this.tailScheduled = false;
    this.tailEverInjected = false;
    this.clearTimer();
  }

  async feedChunk(b64: string): Promise<void> {
    if (!this.ctx || this.finished)
      return;
    if (this.ctx.state === 'suspended') {
      try { await this.ctx.resume(); } catch { }
    }

    // llega audio nuevo → invalida cualquier tail pendiente
    this.tailScheduled = false;
    this.tailEverInjected = false;

    if (this.backpressure === 'drop' && this.queuedSec > this.maxQueueSec)
      return;

    const int16 = this.base64ToInt16(b64);
    const f32 = this.i16ToF32(int16);
    const buf = this.ctx.createBuffer(this.channels, f32.length, this.inRate);
    buf.getChannelData(0).set(f32);

    const myEpoch = this.epoch;
    const src = this.ctx.createBufferSource();
    src.buffer = buf;
    src.connect(this.gain);

    const now = this.ctx.currentTime;
    const when = Math.max(now + this.safetyPadSec, this.lastWhen);
    src.start(when);

    const dur = buf.duration;
    this.lastWhen = when + dur;
    this.queuedSec = Math.max(0, this.lastWhen - now);

    if (!this.isPlaying() && this.queuedSec >= this.startThresholdSec)
      this.isPlaying.set(true);

    src.onended = () => {
      if (myEpoch !== this.epoch)
        return; // callback zombi

      const i = this.sources.indexOf(src);
      if (i >= 0)
        this.sources.splice(i, 1);

      this.armCheck(80);
    };

    this.sources.push(src);

    // marca inicio real y arma watchdog
    this.started = true;
    this.lastChunkTs = performance.now();
    this.armCheck(80);
  }

  endSession(): void {
    this.closed = true;
    this.armCheck(10);
  }

  async stopNow(): Promise<void> {
    this.newEpoch(); // invalida callbacks
    this.closed = true;
    this.finished = true;
    this.started = false;
    this.clearTimer();

    // Detener audio externo
    if (this.externalAudio) {
      try {
        this.externalAudio.pause();
        this.externalAudio.src = "";
        this.externalAudio.load();
      } catch { }
      this.externalAudio = null;
    }

    for (const s of this.sources) {
      try { s.onended = null; } catch { }
      try { s.stop(); } catch { }
    }

    this.sources = [];
    this.queuedSec = 0;
    this.tailScheduled = false;
    this.isPlaying.set(false);
    try { await this.ctx?.suspend(); } catch { }
  }

  // ===== Internos ==========================================================

  private async ensureContext() {
    if (!this.ctx || this.ctx.state === 'closed') {
      this.ctx = new AudioContext({ latencyHint: 'interactive' });
      this.gain = this.ctx.createGain();
      this.gain.connect(this.ctx.destination);
    }

    if (this.ctx.state === 'suspended') {
      try { await this.ctx.resume(); } catch { }
    }
  }

  private newEpoch() {
    this.epoch++;
    this.clearTimer();
    this.tailScheduled = false;
  }

  private armCheck(ms: number) {
    if (this.finished) return;
    this.clearTimer();
    const myEpoch = this.epoch;
    this.ngZone.runOutsideAngular(() => {
      this.checkTimer = window.setTimeout(() => {
        if (myEpoch !== this.epoch)
          return;

        this.maybeFinish();
      }, ms);
    });
  }

  private clearTimer() {
    if (this.checkTimer) { clearTimeout(this.checkTimer); this.checkTimer = null; }
  }

  private maybeFinish() {
    this.checkTimer = null;
    if (this.finished)
      return;

    if (!this.started) {
      this.armCheck(500);
      return;
    }

    const nowMs = performance.now();
    const quiet = this.closed && ((nowMs - this.lastChunkTs) >= this.graceMs);
    const queueEmpty = (this.sources.length === 0);

    if (quiet && queueEmpty) {
      // Si aún no hemos metido tail, mételo una sola vez
      if (!this.tailEverInjected && !this.tailScheduled) {
        this.injectTailAndFinish();
        return;
      }
      // Ya hubo tail (o está acabando): espera un pad y cierra si sigue todo vacío
      const myEpoch = this.epoch;
      const pad = this.getRenderPadMs();
      setTimeout(() => {
        if (myEpoch !== this.epoch || this.finished)
          return;

        const stillEmpty = (this.sources.length === 0);
        const okAudible = !this.hasOutputClock() || this.isAudiblyDrained(0.008);
        if (stillEmpty && okAudible) this.finish();
        else this.armCheck(80);
      }, pad);
      return;
    }

    this.armCheck(queueEmpty ? 120 : 300);
  }

  private finish() {
    if (this.finished)
      return;

    this.finished = true;
    this.started = false;
    this.isPlaying.set(false);
    this.finishedSubj.next();
  }

  // ===== Tail de seguridad =================================================

  private getRenderPadMs(): number {
    if (!this.ctx)
      return 220;

    const sr = this.ctx.sampleRate || 48000;
    const quantumSec = 128 / sr;
    const base = this.ctx.baseLatency ?? 0;
    const out = (this.ctx as any).outputLatency ?? 0;

    // Si no hay reloj real, devolvemos pad conservador.
    if (!this.hasOutputClock())
      return 280;

    const estMs = (base + out + quantumSec) * 1000;
    return Math.max(150, Math.min(350, Math.round(estMs)));
  }

  // 4) CAMBIA injectTailAndFinish(): marca tailEverInjected y NO reinyecta en onended
  private injectTailAndFinish() {
    if (this.finished || this.tailScheduled)
      return;

    this.tailScheduled = true;
    this.tailEverInjected = true; // 👈 importante

    const myEpoch = this.epoch;
    const padMs = this.getRenderPadMs();
    const sr = this.ctx.sampleRate || 48000;
    const frames = Math.max(1, Math.round((padMs / 1000) * sr));

    const tailBuf = this.ctx.createBuffer(1, frames, sr);
    tailBuf.getChannelData(0).fill(0);

    const src = this.ctx.createBufferSource();
    src.buffer = tailBuf;
    src.connect(this.gain);

    const now = this.ctx.currentTime;
    const when = Math.max(now + this.safetyPadSec, this.lastWhen);
    src.start(when);

    const dur = tailBuf.duration;
    this.lastWhen = when + dur;
    this.queuedSec = Math.max(0, this.lastWhen - now);
    this.sources.push(src);

    src.onended = () => {
      if (myEpoch !== this.epoch)
        return; // zombi

      const idx = this.sources.indexOf(src);
      if (idx >= 0)
        this.sources.splice(idx, 1);

      this.tailScheduled = false;

      const hasClock = this.hasOutputClock();
      const drainedNow = (this.sources.length === 0) && (!hasClock || this.isAudiblyDrained(0.008));
      if (drainedNow) {
        this.finish();
        return;
      }

      // NO reinyectar aquí. Dejamos que maybeFinish haga el fallback una única vez.
      this.armCheck(60);
    };
  }

  // ===== Helpers de latencia/clock ========================================

  private outputTimeSec(): number {
    if (!this.ctx)
      return -Infinity;

    const anyCtx = this.ctx as any;
    if (typeof anyCtx.getOutputTimestamp === 'function') {
      const ts = anyCtx.getOutputTimestamp();
      // algunos navegadores tienen getOutputTimestamp pero SIN outputTime usable
      if (ts && typeof ts.outputTime === 'number' && isFinite(ts.outputTime))
        return ts.outputTime;
    }
    return -Infinity;
  }

  private isAudiblyDrained(epsilonSec = 0.008): boolean {
    const out = this.outputTimeSec();
    if (!isFinite(out))
      return false; // sin reloj real → no se puede afirmar

    return out >= (this.lastWhen - epsilonSec);
  }

  private hasOutputClock(): boolean {
    return isFinite(this.outputTimeSec());
  }

  // ===== Utilidades ========================================================

  private base64ToInt16(b64: string): Int16Array {
    const bin = atob(b64);
    const len = bin.length & ~1;
    const buf = new ArrayBuffer(len);
    const u8 = new Uint8Array(buf);
    for (let i = 0; i < len; i++)
      u8[i] = bin.charCodeAt(i);

    return new Int16Array(buf);
  }

  private i16ToF32(i16: Int16Array): Float32Array {
    const n = i16.length;
    let out = this.scratchF32;
    if (!out || out.length !== n)
      out = new Float32Array(n);

    const k = 1 / 32768;
    for (let i = 0; i < n; i++)
      out[i] = i16[i] * k;

    this.scratchF32 = out;
    return out;
  }

  public getAudioBlobFromChunks(audioChunks) {
    const pcmData = audioChunks
      .map(chunk => atob(chunk))
      .map(binaryString => new Uint8Array(binaryString.length)
        .map((_, i) => binaryString.charCodeAt(i)))
      .reduce((acc, array) => {
        const combined = new Uint8Array(acc.length + array.length);
        combined.set(acc, 0);
        combined.set(array, acc.length);
        return combined;
      }, new Uint8Array(0));

    const wavHeader = this.createWAVHeader(pcmData.length);
    const wavData = new Uint8Array(wavHeader.length + pcmData.length);
    wavData.set(wavHeader, 0);
    wavData.set(pcmData, wavHeader.length);

    return new Blob([wavData], { type: 'audio/wav' });
  }

  private createWAVHeader(dataLength: number, sampleRate: number = 24000, numChannels: number = 1): Uint8Array {
    const byteRate = sampleRate * numChannels * 2; // 2 bytes per sample (16-bit PCM)
    const blockAlign = numChannels * 2;

    const buffer = new ArrayBuffer(44);
    const view = new DataView(buffer);

    // ChunkID "RIFF"
    this.writeString(view, 0, 'RIFF');
    view.setUint32(4, 36 + dataLength, true); // ChunkSize
    this.writeString(view, 8, 'WAVE'); // Format

    // Subchunk1ID "fmt "
    this.writeString(view, 12, 'fmt ');
    view.setUint32(16, 16, true); // Subchunk1Size
    view.setUint16(20, 1, true); // AudioFormat (PCM)
    view.setUint16(22, numChannels, true); // NumChannels
    view.setUint32(24, sampleRate, true); // SampleRate
    view.setUint32(28, byteRate, true); // ByteRate
    view.setUint16(32, blockAlign, true); // BlockAlign
    view.setUint16(34, 16, true); // BitsPerSample

    // Subchunk2ID "data"
    this.writeString(view, 36, 'data');
    view.setUint32(40, dataLength, true); // Subchunk2Size

    return new Uint8Array(buffer);
  }

  private writeString(view: DataView, offset: number, string: string): void {
    for (let i = 0; i < string.length; i++) {
      view.setUint8(offset + i, string.charCodeAt(i));
    }
  }

  public saveAudio(finalChunk) {
    const id = finalChunk?.completion_id;
    const audioChunks = finalChunk?.content?.audio_content;
    if (!audioChunks || audioChunks?.length === 0)
      return;

    this.debugService.info(finalChunk, '[PLAYAUDIOHANDLERSERVICE] Saving Final Audio Chunk');
    const blob = this.getAudioBlobFromChunks(audioChunks);
    this.audioStorageService.storeAudio(id, blob);
  }

  public async playAudioOnDemand(audioObject) {
    this.stopAudioOnDemand();

    const { completion_id, audioBlob } = audioObject;
    const audioUrl = URL.createObjectURL(audioBlob);
    this.currentAudioOnDemandElement = new Audio(audioUrl);
    this.currentAudioOnDemandElement?.play();
    this.isPlayingOnDemand.set(completion_id);
    this.currentAudioOnDemandElement.onended = () => {
      URL.revokeObjectURL(audioUrl);
      this.isPlayingOnDemand.set(null);
    };
  }

  public stopAudioOnDemand() {
    this.currentAudioOnDemandElement?.pause();
    this.isPlayingOnDemand.set(null);
  }

  public playExternalAudio(url: string) {
    const onEnded = () => {
      this.isPlaying.set(false);
    }

    if (this.externalAudio) {
      try { 
        this.externalAudio.pause(); 
        this.externalAudio.removeEventListener("ended", onEnded);
        this.externalAudio.removeEventListener("error", onEnded);
      } catch { }
    }
    this.externalAudio = new Audio(url);
    this.externalAudio.addEventListener("ended", onEnded, { once: true });
    this.externalAudio.addEventListener("error", onEnded, { once: true });

    this.externalAudio.play().catch(err => console.warn('[PlayAudioHandler] Error playing external audio:', err));
    this.isPlaying.set(true)
  }

}